import { z } from "zod";
import { SteamId } from "./user.ts";

/**
 * Who may sign in.
 * - open: anyone with a Steam account, except players marked "refused".
 * - allowlist: only players marked "allowed", and the admins.
 */
export const ACCESS_MODES = ["open", "allowlist"] as const;
export type AccessMode = (typeof ACCESS_MODES)[number];

/** waiting: tried to sign in, not let in yet. allowed: may sign in. refused: may not, and is no longer listed as waiting. */
export const ACCESS_STATUSES = ["waiting", "allowed", "refused"] as const;
export type AccessStatus = (typeof ACCESS_STATUSES)[number];

export interface AccessPlayer {
  id: string;
  steamId: string;
  name: string;
  avatarUrl: string | null;
  access: AccessStatus;
  isAdmin: boolean;
  /** Last sign-in or sign-in attempt. */
  lastSeenAt: string;
  createdAt: string;
}

export interface AccessList {
  mode: AccessMode;
  players: AccessPlayer[];
}

export const SetAccessInput = z.object({ access: z.enum(ACCESS_STATUSES) });

/** A Steam ID, or a profile address that contains one: https://steamcommunity.com/profiles/7656…/ */
export function parseSteamIdInput(input: string): string | null {
  const text = input.trim();
  const fromUrl = text.match(/steamcommunity\.com\/profiles\/(\d+)/i)?.[1];
  const candidate = fromUrl ?? text;
  return SteamId.safeParse(candidate).success ? candidate : null;
}

export const AddPlayerInput = z.object({
  steam: z.string().trim().min(1).max(200),
  /** Shown until the player signs in for the first time and Steam gives their real name. */
  name: z.string().trim().max(32).optional(),
});
